import { useState } from 'react';
import { Printer } from 'lucide-react';
import type { Checklist } from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { useDialog } from '../../context/DialogContext';
import { printDocument } from '../../utils/printDocument';
import { buildTaskListPrintHtml, taskListPrintRows } from '../../utils/taskListPrint';

export interface TaskListPrintModalProps {
  checklist: Checklist | null;
  todayKey: string;
  onClose: () => void;
}

/**
 * Print a task list (or save it as a PDF from the print dialog): its tasks in
 * date-and-time order under the day each is due, with a box to tick.
 */
export function TaskListPrintModal({ checklist, todayKey, onClose }: TaskListPrintModalProps) {
  const { alert } = useDialog();
  const [includeDone, setIncludeDone] = useState(false);
  const [includeNotes, setIncludeNotes] = useState(true);

  if (!checklist) return null;
  const count = taskListPrintRows(checklist, includeDone).length;

  async function print() {
    if (!checklist) return;
    const win = printDocument(
      buildTaskListPrintHtml(checklist, { includeDone, includeNotes, todayKey }),
    );
    if (!win) {
      await alert({
        title: 'Could not open the print view',
        message: 'Your browser blocked the window. Allow pop-ups for this site and try again.',
        variant: 'warning',
      });
      return;
    }
    onClose();
  }

  return (
    <EntityModal
      open
      onClose={onClose}
      title="Print task list"
      size="sm"
      footer={
        <div className="flex justify-end gap-2 w-full">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={print}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700"
          >
            <Printer size={14} /> Print
          </button>
        </div>
      }
    >
      <p className="text-sm text-gray-600 -mt-1 mb-4">
        Tasks are printed in order of their due date
        {checklist.dueTimeEnabled ? ' and time' : ''}, under the day each is due. Choose “Save as
        PDF” in the print dialog to keep a copy.
      </p>
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={includeDone}
            onChange={(e) => setIncludeDone(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          Include finished tasks
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={includeNotes}
            onChange={(e) => setIncludeNotes(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          Include notes
        </label>
      </div>
      <p className="mt-4 text-xs text-gray-500">
        {count} task{count === 1 ? '' : 's'} will be printed.
      </p>
    </EntityModal>
  );
}
